import { useTranslation } from 'react-i18next';

import { getEclipsePhase, type LocalEclipseDetails, type LocalEclipseMoment } from '../lib/eclipse';
import type { TerrainVisibilityResult } from '../lib/terrainMath';
import type { CloudReading } from '../lib/weather';
import type { Coordinate, RouteResult } from '../types';
import { RoutePlanner } from './RoutePlanner';

type TerrainState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; result: TerrainVisibilityResult }
  | { status: 'error' };

interface SelectedLocationPanelProps {
  location: Coordinate;
  details: LocalEclipseDetails;
  moment: LocalEclipseMoment | null;
  displayTime: Date;
  cloud: CloudReading | null;
  cloudStatus: 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';
  terrain: TerrainState;
  origin: Coordinate | null;
  pickingOrigin: boolean;
  onClose: () => void;
  onOriginChange: (origin: Coordinate | null) => void;
  onPickingOriginChange: (picking: boolean) => void;
  onRouteChange: (route: RouteResult | null) => void;
}

const formatTime = (date: Date, locale: string, seconds = false) =>
  new Intl.DateTimeFormat(locale, {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
    second: seconds ? '2-digit' : undefined,
    hour12: false,
  }).format(date);

export function SelectedLocationPanel({
  location,
  details,
  moment,
  displayTime,
  cloud,
  cloudStatus,
  terrain,
  origin,
  pickingOrigin,
  onClose,
  onOriginChange,
  onPickingOriginChange,
  onRouteChange,
}: SelectedLocationPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-GB';
  const phase = moment?.phase ?? getEclipsePhase(details, displayTime);
  const typeLabel =
    details.type === 'total'
      ? t('location.total')
      : details.type === 'partial'
        ? t('location.partial')
        : t('location.none');
  const terrainResult = terrain.status === 'ready' ? terrain.result : null;
  const hasTotality = details.centralDurationSeconds > 0;
  const primaryValue = hasTotality
    ? Math.round(details.centralDurationSeconds).toString()
    : `${(details.obscuration * 100).toFixed(1)}%`;
  const primaryLabel = hasTotality ? t('location.totalitySeconds') : t('location.obscuration');

  const cloudReading =
    cloud && cloudStatus === 'ready'
      ? `${cloud.cloudCover}%`
      : cloudStatus === 'loading'
        ? '…'
        : cloudStatus === 'unavailable'
          ? t('weather.forecastDate')
          : '—';
  const terrainReading = terrainResult
    ? t(`terrain.${terrainResult.status}`)
    : terrain.status === 'loading'
      ? '…'
      : '—';

  return (
    <aside className="location-sheet" aria-labelledby="selected-location-title">
      <div className="sheet-handle" aria-hidden="true" />
      <header className="location-hero">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">{t('location.title')}</span>
            <h2 id="selected-location-title">{typeLabel}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            ×
          </button>
        </div>
        <div className="location-hero-readings">
          <div className="primary-reading">
            <strong>{primaryValue}</strong>
            <span>{primaryLabel}</span>
          </div>
          <div className="maximum-reading">
            <span>{t('location.maximum')}</span>
            <strong>
              {details.contacts ? formatTime(details.contacts.maximum, locale, true) : '—'}
            </strong>
            <small>CEST</small>
          </div>
        </div>
        <div className={`phase-banner phase-${phase}`}>
          <span className="phase-dot" aria-hidden="true" />
          {t(`location.phase_${phase}`)}
        </div>
      </header>

      <section className="condition-list" aria-label={t('location.conditions')}>
        <div className={`condition-row eclipse-condition phase-${phase}`}>
          <span className="condition-symbol eclipse-condition-symbol" aria-hidden="true">
            ◐
          </span>
          <span>{t('location.obscurationAtTime')}</span>
          <strong>{((moment?.obscuration ?? 0) * 100).toFixed(1)}%</strong>
        </div>
        <div className="condition-row">
          <span className="condition-symbol" aria-hidden="true">
            ☁
          </span>
          <span>{t('weather.title')}</span>
          <strong>{cloudReading}</strong>
        </div>
        <div className={`condition-row ${terrainResult ? `status-${terrainResult.status}` : ''}`}>
          <span className="condition-symbol" aria-hidden="true">
            ⌁
          </span>
          <span>{t('terrain.atMaximum')}</span>
          <strong>{terrainReading}</strong>
        </div>
        <div className="condition-row">
          <span className="condition-symbol" aria-hidden="true">
            ↗
          </span>
          <span>{t('location.altitude')}</span>
          <strong>{details.sunAltitude?.toFixed(1) ?? '—'}°</strong>
        </div>
      </section>

      {cloudStatus === 'error' && <p className="inline-error">{t('weather.error')}</p>}
      {terrain.status === 'error' && <p className="inline-error">{t('terrain.error')}</p>}

      {details.contacts && (
        <details className="reveal-panel contacts-reveal">
          <summary>
            <span>{t('location.contacts')}</span>
            <strong>{formatTime(details.contacts.maximum, locale)}</strong>
            <span className="reveal-icon" aria-hidden="true">
              ＋
            </span>
          </summary>
          <div className="contacts-row">
            {(['c1', 'c2', 'maximum', 'c3', 'c4'] as const).map((contact) => (
              <div
                className={`${contact === 'maximum' ? 'is-maximum' : ''} ${contact === 'c2' || contact === 'c3' ? 'is-total-contact' : ''} ${details.contacts?.[contact] ? '' : 'is-missing'}`}
                key={contact}
              >
                <span className="contact-code">
                  {contact === 'maximum' ? 'MAX' : contact.toUpperCase()}
                </span>
                <i aria-hidden="true" />
                <strong>
                  {details.contacts?.[contact]
                    ? formatTime(details.contacts[contact], locale, true)
                    : '—'}
                </strong>
                <small>{t(`location.contact_${contact}`)}</small>
              </div>
            ))}
          </div>
        </details>
      )}

      <details className="reveal-panel route-reveal">
        <summary>
          <span>{t('route.title')}</span>
          <span aria-hidden="true">↗</span>
        </summary>
        <RoutePlanner
          key={`${location.lat}:${location.lon}:${origin?.lat ?? ''}:${origin?.lon ?? ''}`}
          destination={location}
          origin={origin}
          pickingOrigin={pickingOrigin}
          onOriginChange={onOriginChange}
          onPickingOriginChange={onPickingOriginChange}
          onRouteChange={onRouteChange}
        />
      </details>
    </aside>
  );
}
